import { h } from 'preact';
import Store from 'store/store';
import * as jsonPointer from 'utils/jsonPointer';
import { canUser } from 'utils/roles';
import { getStyleVariable } from 'utils/thundr';
import { joinClasses } from 'utils/utils';

import Button from 'components/button/button';
import MovieEditor from 'components/editor/movies/movieEditor';
import MoviePageHeader from 'components/editor/movies/movieHeader';
import MoviePageFooter from 'components/editor/movies/movieFooter';
import SelectableSection from 'components/editor/selectableSection';
import MediaField from 'components/editor/settings/mediaField';
import SimpleField from 'components/editor/settings/simpleField';
import ToggleField from 'components/editor/settings/toggleField';
import IconChevronLeft from 'components/icons/chevronLeft';
import IconPlus from 'components/icons/plus';
import InlineSelect from 'components/inlineSelect/inlineSelect';
import Toggle from 'components/toggle/toggle';

// Styling imports will only happen in the final styling file used (i.e. homeEntUniversal.sss or showtimesFocus.sss)
// import s from 'components/editor/movies/watch/watch.sss';

export default class WatchPageEditor extends MovieEditor {
	s = {}; // Styling will only be assigned in the final implementation (i.e. homeEntUniversal.js or showtimesFocus.js)

	/** @type {'showtimes' | 'homeEnt'} */
	type = null;

	constructor(props) {
		super(props);

		this.toggleCustomOnesheet = this.toggleCustomOnesheet.bind(this);
		this.toggleCustomHeaderImage = this.toggleCustomHeaderImage.bind(this);
		this.setBannerType = this.setBannerType.bind(this);
		this.toggleBannerLink = this.toggleBannerLink.bind(this);
		this.resetBanner = this.resetBanner.bind(this);
	}

	getData() {
		return {};
	}

	renderOnesheetTopLinks() {
		return (
			<div class={this.s.topLinks}>
				<div class={this.s.topLink}>
					<IconChevronLeft /> {this.text('$SHOWTIMES_EXIT', 'Movie Details')}
				</div>
			</div>
		);
	}

	renderOnesheet() {
		const data = this.getData();
		const onesheet = data.onesheet?.custom ? data.onesheet?.url : this.movie?.onesheet?.url;

		return (
			<SelectableSection
				{...this.props}
				class={this.s.onesheet}
				section={`/pages/${this.type}/media`}
			>
				{onesheet && <img src={onesheet} />}
				{!onesheet && <span><IconPlus />Add onesheet</span>}
			</SelectableSection>
		);
	}

	renderBanner() {
		const banner = this.getData()?.banner;
		const type = banner?.type || 'image';
		
		let content;
		if (type === 'image') {
			content = !!banner?.image && <img src={banner.image} alt={banner.imageAlt || ''} />;
		} else {
			content = banner?.text;
		}
		if (!content) {
			return (
				<SelectableSection class={joinClasses(this.s.banner, this.s.add)} section={`/pages/${this.type}/banner`} {...this.props}>
					<IconPlus /><span>Add banner</span>
				</SelectableSection>
			);
		}
		return (
			<SelectableSection class={joinClasses(this.s.banner, this.s[`banner-${type}`])} section={`/pages/${this.type}/banner`} {...this.props}>
				{content}
			</SelectableSection>
		);
	}

	renderFooter() {
		return (
			<MoviePageFooter {...this.props} />
		);
	}

	renderTopLinks() {
		return (
			<div class={this.s.topLinks} />
		);
	}

	renderMainHeader() {
		return (
			<div class={this.s.mainHeader} />
		);
	}

	renderMainTop() {
		return (
			<div class={this.s.top}>
				{this.renderTopLinks()}
				{this.renderMainHeader()}
			</div>
		);
	}

	renderMainContent() {}

	renderSide() {
		return (
			<div class={this.s.side}>
				{this.renderOnesheetTopLinks()}
				{this.renderOnesheet()}
			</div>
		);
	}

	renderMain() {
		return (
			<div class={this.s.main}>
				{this.renderMainTop()}
				<div class={this.s.mainContent}>
					{this.renderBanner()}
					{this.renderMainContent()}
				</div>
				{this.renderFooter()}
			</div>
		);
	}

	renderVisual() {
		const stylingValues = {
			'--movie-primary-color': getStyleVariable(this.state.thundrData, `primaryColor`),
			'--header-bg': getStyleVariable(this.state.thundrData, `${this.type}HeaderBgColor`),
		};
		const style = Object.entries(stylingValues).map(([k, v]) => v && `${k}:${v}`).filter(Boolean).join(';');

		return (
			<div class={this.s.visual} style={style}>
				<MoviePageHeader {...this.props} />
				<div class={this.s.content}>
					{this.renderSide()}
					{this.renderMain()}
				</div>
			</div>
		);
	}

	toggleCustomOnesheet(e) {
		const useCustomOnesheet = e.target.checked;
		this.toggleCustomMedia('onesheet', useCustomOnesheet);
	}

	toggleCustomHeaderImage(e) {
		const useCustomHeader = e.target.checked;
		this.toggleCustomMedia('headerImage', useCustomHeader);
	}

	toggleCustomMedia(type, useCustom) {
		const data = Store.get().data;
		const path = `${this.props.dataRoot}/content/pages/${this.type}/${type}`;

		let newValue = null;
		if (useCustom) {
			newValue = this.previousMediaData?.[type] || {};
		} else {
			this.previousMediaData ??= {};
			this.previousMediaData[type] = jsonPointer.get(data, path);
		}

		jsonPointer.setDeep(data, path, newValue);
	}

	renderOnesheetSettings() {
		const readonly = !canUser('write');
		const useCustomOnesheet = this.getData().onesheet?.custom;
		const root = `${this.props.dataRoot}/content/pages/${this.type}/onesheet`;
		const mediaPath = useCustomOnesheet ? `${root}/url` : `${this.props.dataRoot}/content/onesheet/url`;

		return (
			<div class={this.s.mediaField}>
				<h4>Onesheet (Desktop)</h4>
				<ToggleField dark path={`${root}/custom`} disabled={readonly}>Use unique onesheet</ToggleField>
				<MediaField
					dark
					class={this.s.onesheetImage}
					label="Replace image"
					path={mediaPath}
					group={this.props.config?.id}
					assetData={{ type: 'Onesheet', aspectRatio: '350:517', recommendedDimensions: '1400:2068' }}
					disabled={readonly || !useCustomOnesheet}
				/>
			</div>
		);
	}

	renderMediaSettings() {
		return [];
	}

	renderHeaderSettings() {}

	setBannerType(e) {
		jsonPointer.setDeep(Store.get().data, `${this.props.dataRoot}/content/pages/${this.type}/banner/type`, e.target.value);
	}

	toggleBannerLink(e) {
		const enabled = !!e.target.checked;
		if (!enabled) {
			this.previousBannerLink = jsonPointer.get(Store.get().data, `${this.props.dataRoot}/content/pages/${this.type}/banner/link`);
		}
		const value = enabled ? (this.previousBannerLink || 'https://') : null;
		jsonPointer.setDeep(Store.get().data, `${this.props.dataRoot}/content/pages/${this.type}/banner/link`, value);
		if (enabled) {
			this.base.querySelector(`.${this.s.bannerSettings} .${this.s.link} input`)?.focus();
		}
	}

	resetBanner() {
		jsonPointer.setDeep(Store.get().data, `${this.props.dataRoot}/content/pages/${this.type}/banner`, null);
		// Close the settings bar
		this.props.select?.(null);
	}

	renderBannerSettings() {
		const root = `${this.props.dataRoot}/content/pages/${this.type}/banner`;
		const value = this.movie?.pages?.[this.type]?.banner;
		const type = value?.type || 'image';
		const hasValue = type === 'image' ? !!value?.image : !!value?.text;

		let bannerContent;
		if (type === 'image') {
			bannerContent = [
				<MediaField
					dark
					class={this.s.bannerImage}
					label="Banner image"
					path={`${root}/image`}
					group={this.props.config?.id}
					assetData={{ type: 'watch-banner', aspectRatio: '5:1', recommendedDimensions: '2000:400' }}
					key="image"
				/>,
				<SimpleField dark label="Alt text" path={`${root}/imageAlt`} key="imageAlt" />,
			];
		} else {
			bannerContent = [
				<SimpleField dark label="Banner text" placeholder="Banner text" path={`${root}/text`} key="text" />,
			];
		}

		return (
			<div class={this.s.bannerSettings}>
				<InlineSelect dark value={type} onChange={this.setBannerType}>
					<option value="image">Image Banner</option>
					<option value="text">Text Banner</option>
				</InlineSelect>
				{bannerContent}
				<SimpleField dark class={this.s.link} disabled={!value?.link} label="Link target" path={`${root}/link`} placeholder="https://example.com" key="link" />
				<Toggle dark class={this.s.toggleLink} checked={value?.link} onInput={this.toggleBannerLink} key="toggleLink">Enable linkout</Toggle>
				<Button dark danger text class={this.s.remove} disabled={!hasValue} onClick={this.resetBanner} key="remove">Remove banner</Button>
			</div>
		);
	}

	getSettingsDetails() {
		switch (this.props.activeElement) {
			case `/pages/${this.type}/media`:
				return {
					nav: [{ label: 'Media', path: `/pages/${this.type}/media}` }],
					content: this.renderMediaSettings(),
				};
			case `/pages/${this.type}/header`:
				return {
					nav: [{ label: 'Header', path: `/pages/${this.type}/header` }],
					content: this.renderHeaderSettings(),
				};
			case `/pages/${this.type}/banner`:
				return {
					nav: [{ label: 'Banner', path: `/pages/${this.type}/banner` }],
					content: this.renderBannerSettings(),
				};
		}
	}

	renderSettings() {
		if (this.props.activeElement.startsWith('/header')) {
			return <MoviePageHeader {...this.props} />;
		}
		if (this.props.activeElement.startsWith('/footer')) {
			return <MoviePageFooter {...this.props} />;
		}

		const details = this.getSettingsDetails();
		return (
			<div class={this.s.settings}>
				{this.renderSettingsNavigation(details?.nav)}
				{details?.content}
			</div>
		);
	}
}